
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const MagazineTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  const invoiceNum = metaData.find(m => m.label.includes('#') || m.label.includes('Ref'))?.value || '001';

  return (
    <div className={containerClass}>
       <div className="p-12 flex flex-col h-full relative">
           {!hideHeader && (
             <>
               {/* Massive Background Number */}
               <div className="absolute top-[-50px] right-[-20px] text-[250px] font-black text-neutral-50 pointer-events-none z-0 leading-none">
                   {invoiceNum.replace(/\D/g, '').slice(-2)}
               </div>

               <div className="relative z-10 border-b-4 border-black pb-6 mb-12 flex justify-between items-end">
                   <div>
                       <h1 className="text-6xl font-black uppercase tracking-tighter leading-[0.8] mb-2">{type}</h1>
                       <p className="font-serif italic text-xl text-neutral-500">issue no. {invoiceNum}</p>
                   </div>
                   <div className="text-right">
                       <h2 className="font-bold text-lg uppercase tracking-wide">{profile.name}</h2>
                       <p className="text-xs font-bold text-neutral-400 uppercase">{profile.address}</p>
                   </div>
               </div>

               <div className="grid grid-cols-12 gap-8 mb-16 relative z-10">
                   <div className="col-span-4">
                       <div className="bg-black text-white p-6 h-full flex flex-col justify-center">
                           <p className="font-bold uppercase text-xs tracking-widest mb-4 opacity-70">Client Profile</p>
                           {client ? (
                               <>
                                   <p className="text-2xl font-serif font-bold leading-tight mb-2">{client.name}</p>
                                   <p className="text-sm opacity-80">{client.company}</p>
                                   <p className="text-xs opacity-60 mt-4">{client.address}</p>
                               </>
                           ) : <p className="opacity-50">NO CLIENT</p>}
                       </div>
                   </div>
                   <div className="col-span-8 flex flex-col justify-center pl-4">
                       <p className="text-4xl font-serif font-bold text-neutral-900 leading-tight mb-4">"{title}"</p>
                       <div className="flex gap-8">
                           {metaData.filter(m => !m.label.includes('#')).map((m, i) => (
                               <div key={i}>
                                   <p className="text-xs font-black uppercase tracking-widest text-neutral-400 mb-1">{m.label}</p>
                                   <p className="font-bold text-lg">{m.value}</p>
                               </div>
                           ))}
                       </div>
                   </div>
               </div>
             </>
           )}

           <div className="flex-1 magazine-table relative z-10">
               {children}
           </div>

           <div className="mt-auto border-t-2 border-black pt-6 flex justify-between items-center relative z-10">
               <p className="font-serif italic text-lg text-neutral-700">"{footerNote}"</p>
               <div className="flex items-center gap-2">
                   <div className="w-3 h-3 bg-black rounded-full"></div>
                   <div className="w-3 h-3 bg-neutral-300 rounded-full"></div>
                   <div className="w-3 h-3 bg-neutral-100 rounded-full"></div>
               </div>
           </div>
       </div>
    </div>
  );
};

export default MagazineTemplate;
